import {
  Bell,
  BookCopy,
  BookMarked,
  BookOpen,
  Brain,
  Calendar,
  CalendarDays,
  ClipboardCheck,
  FileText,
  GraduationCap,
  Home,
  PenTool,
  Users,
  Edit3,
} from 'lucide-react';
import type { RouteConfig } from '../types';
import {
  AddFinalGrades,
  AnecdotalRecord,
  AttendanceSheet,
  DisciplineRecord,
  MessageParent,
  MyStudents,
  SpeedGradingView,
  StudentReportCards,
  SubjectHomePage,
  TeacherAnnouncements,
  TeacherAssessments,
  TeacherCreateAssessment,
  TeacherDashboard,
  TeacherGradeAssessments,
  TeacherLeaveApplication,
  TeacherModules,
  TeacherOpenAssessment,
  TeacherProfile,
  TeacherSchoolCalendar,
  TeacherStudentsInSubjects,
  TeacherSubjectAnnouncements,
  TeacherSubjectsPage,
  TeacherSyllabus,
  TeacherTakeAttendance,
  TeacherWeeklyPlanner,
  TeacherWeightage,
  TelephoneRecords,
  TempPage,
  UpdateAttendance,
  EnterMarks,
  ExamDetails,
  EnterMarksInExam,
} from './lazy-components';

export const teacherBothRoutes: Record<'home' | 'subject', RouteConfig[]> = {
  home: [
    {
      isLabel: true,
      name: 'Home',
      showOnSideBar: true,
    },
    {
      path: '/dashboard',
      name: 'Dashboard',
      icon: Home,
      showOnSideBar: true,
      component: TeacherDashboard,
      isCombobox: true,
      breadcrumb: {
        title: 'Dashboard',
        parent: null,
      },
    },
    {
      path: '/announcements',
      name: 'Announcements',
      icon: Bell,
      showOnSideBar: true,
      component: TeacherAnnouncements,
      isCombobox: true,
      breadcrumb: {
        title: 'Announcements',
        parent: '/dashboard',
      },
    },
    {
      path: '/general-school-calendar',
      name: 'School Calendar',
      icon: Calendar,
      showOnSideBar: true,
      component: TeacherSchoolCalendar,
      isCombobox: true,
      breadcrumb: {
        title: 'School Calendar',
        parent: '/dashboard',
      },
    },
    {
      path: '/weekly-planner',
      name: 'Weekly Planner',
      icon: CalendarDays,
      showOnSideBar: true,
      component: TeacherWeeklyPlanner,
      isCombobox: true,
      breadcrumb: {
        title: 'Weekly Planner',
        parent: '/dashboard',
      },
    },
    {
      path: '/profile',
      name: 'Profile',
      showOnSideBar: false,
      component: TeacherProfile,
      isCombobox: false,
      breadcrumb: {
        title: 'Profile',
        parent: '/dashboard',
      },
    },
    {
      isLabel: true,
      name: 'MY CLASSROOM',
      showOnSideBar: true,
    },
    {
      path: '/subjects/all',
      name: 'My Subjects',
      icon: GraduationCap,
      showOnSideBar: true,
      component: TeacherSubjectsPage,
      isCombobox: true,
      breadcrumb: {
        title: 'My Subjects',
        parent: '/dashboard',
      },
    },
    {
      path: '/my-students',
      name: 'My Students',
      icon: Users,
      showOnSideBar: true,
      component: MyStudents,
      isCombobox: true,
      breadcrumb: {
        title: 'My Students',
        parent: '/dashboard',
      },
    },
    {
      isLabel: true,
      name: 'Attendance',
      showOnSideBar: true,
    },
    {
      isGroup: true,
      groupName: 'Attendance',
      name: 'Attendance',
      icon: Calendar,
      showOnSideBar: true,
      state: 'attendancepages',
      children: [
        {
          path: '/attendance/take-attendance',
          name: 'Take Attendance',
          icon: Calendar,
          showOnSideBar: true,
          component: TeacherTakeAttendance,
          isCombobox: true,
          breadcrumb: {
            title: 'Take Attendance',
            parent: '/dashboard',
          },
        },
        {
          path: '/attendance/update-attendance',
          name: 'Update Attendance',
          icon: Calendar,
          showOnSideBar: true,
          component: UpdateAttendance,
          isCombobox: true,
          breadcrumb: {
            title: 'Update Attendance',
            parent: '/dashboard',
          },
        },
        {
          path: '/attendance/attendance-sheet',
          name: 'Attendance Sheet',
          icon: Calendar,
          showOnSideBar: true,
          component: AttendanceSheet,
          isCombobox: true,
          breadcrumb: {
            title: 'Attendance Sheet',
            parent: '/dashboard',
          },
        },
      ],
    },
    {
      isLabel: true,
      name: 'MISCELLANEOUS',
      showOnSideBar: true,
    },
    {
      path: '/library',
      name: 'Library',
      icon: BookMarked,
      showOnSideBar: true,
      component: TempPage,
      isCombobox: true,
      breadcrumb: {
        title: 'Library',
        parent: '/dashboard',
      },
    },
    {
      path: '/leave-application',
      name: 'Leave Application',
      icon: Calendar,
      showOnSideBar: true,
      component: TeacherLeaveApplication,
      isCombobox: true,
      breadcrumb: {
        title: 'Leave Application',
        parent: '/dashboard',
      },
    },
    {
      path: '/report-cards',
      name: 'Student Report Cards',
      icon: FileText,
      showOnSideBar: true,
      component: StudentReportCards,
      isCombobox: true,
      breadcrumb: {
        title: 'Student Report Cards',
        parent: '/dashboard',
      },
    },
  ],
  subject: [
    {
      isLabel: true,
      name: 'Navigation',
      showOnSideBar: true,
    },
    {
      path: '/dashboard',
      name: 'Back to Dashboard',
      icon: Home,
      showOnSideBar: true,
      component: TeacherDashboard,
      isCombobox: true,
    },
    {
      path: '/subjects/all',
      name: 'All Subjects',
      icon: BookOpen,
      showOnSideBar: true,
      component: TeacherSubjectsPage,
      isCombobox: true,
    },
    {
      isLabel: true,
      name: 'Subject Content',
      showOnSideBar: true,
    },
    {
      path: '/subjects/home/:subjectId',
      name: 'Subject Home',
      icon: GraduationCap,
      showOnSideBar: true,
      component: SubjectHomePage,
      isCombobox: true,
      breadcrumb: {
        title: 'Subject Home',
        parent: '/subjects/all',
      },
    },
    {
      path: '/subjects/announcements/:subjectId',
      name: 'Announcement',
      icon: Bell,
      showOnSideBar: true,
      component: TeacherSubjectAnnouncements,
      isCombobox: true,
      breadcrumb: {
        title: 'Announcements',
        parent: '/subjects/home/:subjectId',
      },
    },
    {
      isGroup: true,
      groupName: 'Grading',
      name: 'Grading',
      icon: FileText,
      showOnSideBar: false, // Hidden for now as requested
      state: 'gradingpages',
      children: [
        {
          path: '/subjects/grading/add-grades/:subjectId',
          name: 'Add Grades',
          icon: FileText,
          showOnSideBar: false,
          component: AddFinalGrades,
          isCombobox: true,
          breadcrumb: {
            title: 'Add Grades',
            parent: '/subjects/home/:subjectId',
          },
        },
        {
          path: '/subjects/grading/weightage/:subjectId',
          name: 'Weightage',
          icon: FileText,
          showOnSideBar: false,
          component: TeacherWeightage,
          isCombobox: true,
          breadcrumb: {
            title: 'Weightage',
            parent: '/subjects/home/:subjectId',
          },
        },
      ],
    },
    {
      path: '/subjects/my-students/:subjectId',
      name: 'My Students',
      icon: Users,
      showOnSideBar: true,
      component: TeacherStudentsInSubjects,
      isCombobox: true,
      breadcrumb: {
        title: 'My Students',
        parent: '/subjects/home/:subjectId',
      },
    },
    {
      path: '/subjects/syllabus/:subjectId',
      name: 'Syllabus',
      icon: BookCopy,
      showOnSideBar: true,
      component: TeacherSyllabus,
      isCombobox: true,
      breadcrumb: {
        title: 'Syllabus',
        parent: '/subjects/home/:subjectId',
      },
    },
    {
      path: '/subjects/modules/:subjectId',
      name: 'Modules',
      icon: Brain,
      showOnSideBar: true,
      component: TeacherModules,
      isCombobox: true,
      breadcrumb: {
        title: 'Modules',
        parent: '/subjects/home/:subjectId',
      },
    },
    {
      path: '/subjects/:subjectId/assessments/speed-grade/:assessmentId',
      name: 'Speed Grade',
      showOnSideBar: false,
      component: SpeedGradingView,
      isCombobox: false,
      breadcrumb: {
        title: 'Speed Grade',
        parent: '/subjects/:subjectId/assessments',
      },
    },
    {
      path: '/subjects/:subjectId/assessments/grade-assessment/:assessmentId',
      name: 'Grade Assessment',
      showOnSideBar: false,
      component: TeacherGradeAssessments,
      isCombobox: false,
      breadcrumb: {
        title: 'Grade Assessment',
        parent: '/subjects/:subjectId/assessments',
      },
    },
    {
      path: '/subjects/:subjectId/assessments/open-assessment/:assessmentId',
      name: 'Open Assessment',
      showOnSideBar: false,
      component: TeacherOpenAssessment,
      isCombobox: false,
      breadcrumb: {
        title: 'Open Assessment',
        parent: '/subjects/:subjectId/assessments',
      },
    },
    {
      path: '/subjects/:subjectId/assessments/create-new-assessment',
      name: 'Create Assessment',
      showOnSideBar: false,
      component: TeacherCreateAssessment,
      isCombobox: false,
      breadcrumb: {
        title: 'Create Assessment',
        parent: '/subjects/:subjectId/assessments',
      },
    },
    {
      path: '/subjects/:subjectId/assessments',
      name: 'Assessments',
      icon: ClipboardCheck,
      showOnSideBar: true,
      component: TeacherAssessments,
      isCombobox: true,
      breadcrumb: {
        title: 'Assessments',
        parent: '/subjects/home/:subjectId',
      },
    },
    {
      path: '/subjects/:subjectId/enter-marks',
      name: 'Enter Marks',
      icon: Edit3,
      showOnSideBar: true,
      component: EnterMarks,
      isCombobox: true,
      breadcrumb: {
        title: 'Enter Marks',
        parent: '/subjects/home/:subjectId',
      },
    },
    {
      path: '/subjects/:subjectId/enter-marks/exam-details/:examId',
      name: 'Exam Details',
      showOnSideBar: false,
      component: ExamDetails,
      isCombobox: false,
      breadcrumb: {
        title: 'Exam Details',
        parent: '/subjects/:subjectId/enter-marks',
      },
    },
    {
      path: '/subjects/:subjectId/enter-marks/enter/:examId',
      name: 'Enter Marks in Exam',
      showOnSideBar: false,
      component: EnterMarksInExam,
      isCombobox: false,
      breadcrumb: {
        title: 'Enter Marks in Exam',
        parent: '/subjects/:subjectId/enter-marks/exam-details/:examId',
      },
    },
    {
      path: '/subjects/:subjectId/projects/grade-project/:projectId',
      name: 'Grade Project',
      showOnSideBar: false,
      component: TempPage,
      isCombobox: false,
    },
    {
      path: '/subjects/:subjectId/projects/speed-grade/:projectId',
      name: 'Speed Grade Project',
      showOnSideBar: false,
      component: TempPage,
      isCombobox: false,
    },
    {
      path: '/subjects/:subjectId/projects/open-project/:projectId',
      name: 'Open Project',
      showOnSideBar: false,
      component: TempPage,
      isCombobox: false,
    },
    {
      path: '/subjects/:subjectId/projects/create-new-project/:projectId',
      name: 'Create Project',
      showOnSideBar: false,
      component: TempPage,
      isCombobox: false,
    },
    {
      path: '/subjects/:subjectId/quizzes/:quizId',
      name: 'Quiz Details',
      showOnSideBar: false,
      component: TempPage,
      isCombobox: false,
    },
    {
      path: '/subjects/:subjectId/quizzes/new',
      name: 'New Quiz',
      showOnSideBar: false,
      component: TempPage,
      isCombobox: false,
    },
    {
      path: '/subjects/:subjectId/quizzes',
      name: 'Quizzes',
      icon: PenTool,
      showOnSideBar: true,
      component: TempPage,
      isCombobox: true,
    },
    {
      path: '/subjects/:subjectId',
      name: 'Subject Details',
      showOnSideBar: false,
      component: TempPage,
      isCombobox: false,
    },
    // Student Action Routes
    {
      path: '/subjects/:subjectId/students/:studentId/telephone-records',
      name: 'Telephone Records',
      showOnSideBar: false,
      component: TelephoneRecords,
      isCombobox: false,
      breadcrumb: {
        title: 'Telephone Records',
        parent: '/subjects/my-students/:subjectId',
      },
    },
    {
      path: '/subjects/:subjectId/students/:studentId/messages',
      name: 'Message Parent',
      showOnSideBar: false,
      component: MessageParent,
      isCombobox: false,
      breadcrumb: {
        title: 'Message Parent',
        parent: '/subjects/my-students/:subjectId',
      },
    },
    {
      path: '/subjects/:subjectId/students/:studentId/discipline',
      name: 'Discipline Record',
      showOnSideBar: false,
      component: DisciplineRecord,
      isCombobox: false,
      breadcrumb: {
        title: 'Discipline Record',
        parent: '/subjects/my-students/:subjectId',
      },
    },
    {
      path: '/subjects/:subjectId/students/:studentId/anecdotal',
      name: 'Anecdotal Record',
      showOnSideBar: false,
      component: AnecdotalRecord,
      isCombobox: false,
      breadcrumb: {
        title: 'Anecdotal Record',
        parent: '/subjects/my-students/:subjectId',
      },
    },
  ],
};
export const teacherRoutes = [...teacherBothRoutes.home, ...teacherBothRoutes.subject];
